/**
 * <MoverIdLabel> — Camera-facing 3D text label that shows a mover's
 * `id` above the mover GLB. Mounted by `<XtsMover>` when
 * `DisplayOptions.showMoverIds === true`.
 *
 * Renders via drei's `<Billboard>` + `<Text>` (troika-three-text under
 * the hood) so the label stays readable as the camera orbits. All
 * styling is driven by `MoverIdLabelOptions`; the component supplies
 * the documented defaults (15 mm black text, 55 mm above the mover's
 * magnet-plate origin, Roboto Condensed via jsDelivr).
 */

import React from 'react';
import { Billboard, Text } from '@react-three/drei';
import { DEFAULT_LABEL_FONT_URL } from './DimensionLabel.js';
import type { MoverIdLabelOptions, Vec3 } from '../../types.js';

const DEFAULT_OFFSET_MM: Vec3 = [0, 0, 55];
const DEFAULT_FONT_SIZE_MM = 15;
const DEFAULT_COLOR = '#000000';
const DEFAULT_OUTLINE_COLOR = '#FFFFFF';

interface MoverIdLabelProps {
  /** The mover's `id` — rendered verbatim. */
  text: string;
  options?: MoverIdLabelOptions;
}

export const MoverIdLabel: React.FC<MoverIdLabelProps> = ({
  text,
  options,
}) => {
  const fontSizeMm = options?.fontSizeMm ?? DEFAULT_FONT_SIZE_MM;
  const color = options?.color ?? DEFAULT_COLOR;
  const offset = options?.offsetMm ?? DEFAULT_OFFSET_MM;
  const fontUrl = options?.fontUrl ?? DEFAULT_LABEL_FONT_URL;
  const outlineWidthMm = options?.outlineWidthMm ?? 0;
  const outlineColor = options?.outlineColor ?? DEFAULT_OUTLINE_COLOR;

  return (
    <Billboard
      position={[offset[0], offset[1], offset[2]]}
      follow
      lockX={false}
      lockY={false}
      lockZ={false}
    >
      <Text
        fontSize={fontSizeMm}
        color={color}
        anchorX="center"
        anchorY="middle"
        font={fontUrl}
        {...(outlineWidthMm > 0
          ? { outlineWidth: outlineWidthMm, outlineColor }
          : {})}
      >
        {text}
      </Text>
    </Billboard>
  );
};
